import { FC, useRef, useState, CSSProperties, ReactNode } from 'react';
import { IFeatureSwiperReviews } from 'mod/featureV2/interface/swiperReviews';
import { FeatureContainer } from 'mod/featureV2/FeatureContainer';
import { IThemeSwiperRef, ThemeSwiper } from '@base/lib/ThemeSwiper';
import cn from 'classnames';
import { Rate } from '@base/lib/Rate'
import ReviewDetailModal from '@app/features/ReviewDetailModal';
import Image from '@base/lib/ThemeImage'
import { IFeatureProps } from 'mod/featureV2/interface';
import { isPhone } from 'base/utils/client';
import { LoopRender } from '@base/snippets/LoopRender';
import { get375CssValue } from 'base/utils/dom-size';
import Marquee from 'react-fast-marquee';
import Tracker from '@base/lib/Tracker';
import { getModuleCode } from 'mod/feature/utils';

type IListItem = IFeatureSwiperReviews['list'][number];

type IItemProps = {
    item: IListItem;
    index: number;
    moduleCode: string;
    onClickCard: (item: IListItem) => void;
    style?: CSSProperties;
}

const itemWrapDefaultClass = 'g-item';

function anonymizeName(name: string): string {
    if (name.length <= 1) return '*';
    return name[0] + '***' + name[name.length - 1];
}

function getPrevElem(activeElem: HTMLDivElement): HTMLDivElement | null {
    let elem = activeElem.previousElementSibling as HTMLDivElement;
    if (!elem) {
        // 寻找class=.rfm-marquee 的祖先元素
        const marqueeElem = activeElem.closest('.rfm-marquee');
        if (marqueeElem) {
            const prevMarqueeElem = marqueeElem.previousElementSibling;
            if (prevMarqueeElem) {
                elem = prevMarqueeElem.querySelector('.g-item:last-child') as HTMLDivElement;
            }
        }
    }
    return elem;
}

function getPrev2Elem(activeElem: HTMLDivElement): HTMLDivElement | null {
    const prevElem = getPrevElem(activeElem);
    if (prevElem) {
        return getPrevElem(prevElem);
    }
    return null;
}

function getNextElem(activeElem: HTMLDivElement): HTMLDivElement | null {
    let elem = activeElem.nextElementSibling as HTMLDivElement;
    if (!elem) {
        // 寻找class=.rfm-marquee 的祖先元素
        const marqueeElem = activeElem.closest('.rfm-marquee');
        if (marqueeElem) {
            const nextMarqueeElem = marqueeElem.nextElementSibling;
            if (nextMarqueeElem) {
                elem = nextMarqueeElem.querySelector('.g-item:first-child') as HTMLDivElement;
            }
        }
    }
    return elem;
}

function addElemClass(elem: HTMLDivElement | null, newClass: string) {
    if (elem) {
        elem.className = cn(itemWrapDefaultClass, newClass);
    }
}

function removeElemClass(elem: HTMLDivElement | null) {
    if (elem) {
        elem.className = itemWrapDefaultClass;
    }
}

const ItemWrap = ({ style, children }: { style: CSSProperties; children: ReactNode; }) => {
    const elemRef = useRef<HTMLDivElement>(null);
    const timeoutId = useRef<ReturnType<typeof setTimeout>>();

    const handleMouseEnter = () => {
        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
        }
        // 延迟 200ms 执行，防止快速滑动时频繁触发
        timeoutId.current = setTimeout(() => {
            const elem = elemRef.current as HTMLDivElement;
            addElemClass(elem, 'g-active');
            addElemClass(getPrevElem(elem), 'g-prev');
            addElemClass(getPrev2Elem(elem), 'g-prev-2');
            addElemClass(getNextElem(elem), 'g-next');
        }, 0);
    }

    const handleMouseLeave = () => {
        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
        }
        timeoutId.current = setTimeout(() => {
            const elem = elemRef.current as HTMLDivElement;
            removeElemClass(elem);
            removeElemClass(getPrevElem(elem));
            removeElemClass(getPrev2Elem(elem));
            removeElemClass(getNextElem(elem));
        }, 0);
    }

    return (
        <div
            ref={elemRef}
            className={'g-item'}
            style={style}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}
        </div>
    )
};

const Card = ({ item, index, moduleCode, onClickCard, style }: IItemProps) => {
    return (
        <Tracker
            data={{
                targetType: 'variant',
                target: item.variantId,
                relTarget: item.variantId,
                module: moduleCode,
                position: `${index + 1}`,
            }}
            onClick={() => onClickCard(item)}
        >
            <div
                className={'g-card pointer relative ctr-bottom'}
                style={style}
            >
                <Image
                    fit={'cover'}
                    src={item?.images?.[0].picUrl}
                    width={isPhone ? 228 : 168}
                    height={isPhone ? 228 : 203}
                    fixed
                />

                <div className={'g-info'}>
                    <div className={'g-name-rate flex flex-justify'}>
                        <span className={'g-user-name'}>{anonymizeName(item.userName)}</span>
                        <Rate value={item.rate} starSize={false}/>
                    </div>
                    <p className={'g-content'}>{item.content}</p>
                </div>
            </div>
        </Tracker>
    )
};

export const FeatureSwiperReviews: FC<IFeatureProps<IFeatureSwiperReviews>> = (props) => {
    const swiperRef = useRef<IThemeSwiperRef>(null);
    const [reviewItem, setReviewItem] = useState<IListItem>();
    const [detailVisible, setDetailVisible] = useState<boolean>(false)

    const { config, list } = props.feature;

    const {
        speed = 50,
        gutter = 20,
    } = config || {};

    const onClickCard = (item: IListItem) => {
        setReviewItem(item)
        setDetailVisible(true)
    }

    const onCloseDetailModal = () => {
        setDetailVisible(false)
    }

    return (
        <FeatureContainer {...props}>

            <ReviewDetailModal
                reviewItem={reviewItem}
                visible={detailVisible}
                onCloseModal={onCloseDetailModal}
            />

            {isPhone ? (
                <ThemeSwiper
                    ref={swiperRef}
                    className={'g-reviews-swiper'}
                    slidesPerView={'auto'}
                    slidesPerGroup={1}
                    spaceBetween={gutter}
                    list={list}
                    scrollbar={isPhone}
                    render={(item: IListItem, index) => (
                        <Card
                            item={item}
                            index={index}
                            moduleCode={getModuleCode(props.feature)}
                            onClickCard={onClickCard}
                        />
                    )}
                />
            ) : (
                <Marquee speed={speed} autoFill={false} pauseOnHover>
                    <LoopRender
                        className={'g-list flex flex-top'}
                        list={list}
                        render={(item: IListItem, { index }) => (
                            <ItemWrap
                                style={{
                                    paddingLeft: get375CssValue(gutter / 2),
                                    paddingRight: get375CssValue(gutter / 2)
                                }}
                            >
                                <Card
                                    item={item}
                                    index={index}
                                    moduleCode={getModuleCode(props.feature)}
                                    onClickCard={onClickCard}
                                />
                            </ItemWrap>
                        )}
                    />
                </Marquee>
            )}
        </FeatureContainer>
    );
};